import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors, media } from '../../ui-components';
import LoadSpinner from '../load-spinner';
import { primaryButtonStyles } from '../../ui-components/components/component-styles/button';

/**
 * Component that renders an embedded Marketo form
 *
 * @component MarketoForm
 *
 * @param {object} props
 *
 * @prop {string} formId
 */
const MarketoForm = props => {
  const { formId, submissionText, setFormComplete } = props;
  const redirectUrl = props.redirectUrl || '';
  const [isLoaded, setIsLoaded] = useState(false);
  const [isFormPresent, setFormPresent] = useState(false);
  const baseURL = 'https://info.credera.com';

  // Creates the script element that runs the Marketo embed code and appends the script to the head
  const loadScript = () => {
    // Create a promise to load jQuery
    const loadJQuery = new Promise((resolve, reject) => {
      const jq = document.createElement('script');
      jq.src = 'https://code.jquery.com/jquery-3.6.4.min.js';
      jq.type = 'text/javascript';
      jq.async = true;
      jq.onload = resolve; // Resolve when jQuery is loaded
      jq.onerror = reject; // Reject if there's an error loading jQuery
      document.head.appendChild(jq);
    });

    // Load forms2.min.js after jQuery has been successfully loaded
    loadJQuery
      .then(() => {
        const mktoScript = document.createElement('script');
        mktoScript.id = 'mktoForms';
        mktoScript.type = 'text/javascript';
        mktoScript.async = true;
        mktoScript.src = `${baseURL}/js/forms2/js/forms2.min.js`;
        mktoScript.onload = () => setIsLoaded(true); // Set isLoaded to true when forms2.min.js loads
        mktoScript.onerror = () => console.error('Error loading forms2.min.js');
        document.head.appendChild(mktoScript);
      })
      .catch(() => console.error('Error loading jQuery'));
  };

  useEffect(() => {
    // On initial mount, load the Marketo form script if not already loaded
    if (!document.getElementById('mktoForms')) {
      loadScript();
    } else {
      setIsLoaded(true);
    }
  }, [isLoaded]);

  useEffect(() => {
    if (isLoaded) {
      // Add a delay of 1 second before checking if the form is loaded
      setTimeout(() => {
        window.MktoForms2.loadForm(baseURL, '248-XIT-367', formId, form => {
          // Attach whenRendered handler inside the loadForm callback
          window.MktoForms2.whenRendered(form => {
            // deleting all <style> elements and <link> elements giving default styling to the form
            const formElement = document.getElementById(`mktoForm_${formId}`);
            const styleElements = formElement?.querySelectorAll('style');
            styleElements[0]?.remove();
            document.getElementById('mktoForms2BaseStyle')?.remove();
            document.getElementById('mktoForms2ThemeStyle')?.remove();
            setFormPresent(true);

            // Add fade-in class to the form element
            const formElementWrapper = document.getElementById(`mktoForm_${formId}`);
            formElementWrapper.classList.add('fade-in');
          });
          // Ensure only one `whenReady` call
          window.MktoForms2.whenReady(function (readyForm) {
            // Set the onSubmit handler
            readyForm.onSubmit(function (submittedValues) {
              // Creating a thank you message and replacing all form elements with this message
              const submitMessage = document.createElement('p');
              submitMessage.appendChild(
                document.createTextNode(submissionText || 'Thank you for submitting! We aim to get back to you ASAP.')
              );
              const formWrapper = document.getElementById('marketo-form-wrapper');
              formWrapper.replaceChildren(submitMessage);

              setFormComplete(true); // Set formComplete to true upon submission

              // returning false to not have the page reload
              return false;
            });
            readyForm.onSuccess(() => {
              if (redirectUrl && typeof window !== 'undefined') {
                window.location.href = redirectUrl;
                return false; // Prevent the default follow-up
              }
              // returning false on success will not have the page reload
              return false;
            });
          });
        });
      }, 400); // Delay to show spinner
    }
  }, [isLoaded, formId]);

  return (
    <MarketoFormWrapper id={'marketo-form-wrapper'}>
      {!isFormPresent && <LoadSpinner size={50} />}
      <form id={`mktoForm_${formId}`} className="form-element"></form>
    </MarketoFormWrapper>
  );
};
export default MarketoForm;

const MarketoFormWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;

  .form-element {
    opacity: 0; /* Initially hidden */
    transition: opacity 0.5s ease-in-out; /* Smooth fade-in effect */
  }

  .form-element.fade-in {
    opacity: 1; /* Fully visible when loaded */
  }

  .mktoForm {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 28px;
    align-self: stretch;
    width: 100% !important;

    @media screen and (max-width: ${media.mediumTop}) {
      gap: 16px;
    }
  }

  .mktoFormRow,
  .mktoButtonRow {
    display: flex;
    justify-content: center;
    gap: 28px;
    width: 100%;

    @media screen and (max-width: ${media.mediumTop}) {
      flex-direction: column;
      gap: 16px;
    }
  }

  .mktoFieldWrap {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .mktoFormCol {
    width: 100%;
  }

  .mktoLabel {
    color: ${colors.deepSmoke};
    font-family: Lato, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    display: flex;
    align-items: center;
    width: 100% !important;
  }

  .mktoField {
    padding-left: 15px;
    padding-right: 15px;
  }

  .mktoTextField,
  .mktoEmailField,
  .mktoTelField {
    border: 1px solid ${colors.gray3};
    width: 100% !important;
    height: 52px;
    font-family: Lato, sans-serif;
    font-size: 16px;
  }

  .mktoLogicalField {
    width: 100% !important;

    label {
      margin-left: 10px;
      font-family: Lato, sans-serif;
      font-size: 14px;
      font-weight: 700;
      color: #3a3a3a;
    }
  }

  div:has(input[name='uTMCampaign']),
  div:has(input[name='uTMContent']),
  div:has(input[name='uTMMedium']),
  div:has(input[name='uTMSource']),
  .mktoAsterix,
  .mktoPlaceholder.mktoPlaceholderState {
    display: none;
  }

  .mktoRequiredField .mktoAsterix {
    display: block;
  }

  .mktoHtmlText {
    width: 100% !important;
  }

  .mktoErrorMsg {
    color: ${colors.crederaRed};
  }

  span {
    margin-left: 0 !important;
  }

  textarea {
    height: 140px !important;
    width: 100% !important;
    border: 1px solid ${colors.gray3};
    font-family: Lato, sans-serif;
    font-size: 16px;
    padding-top: 10px;
    padding-bottom: 10px;
  }

  button {
    ${primaryButtonStyles};
  }

  select {
    background-color: ${colors.pureWhite};
    border: 1px solid ${colors.gray3};
    width: 100% !important;
    height: 52px;
    font-family: Lato, sans-serif;
    padding-left: 15px;
    padding-right: 15px;
    font-size: 16px;
  }
`;
